"use client"

import * as React from "react"
import { ArrowLeft, Search } from "lucide-react"
import * as SheetPrimitive from "@radix-ui/react-dialog"

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"

/**
 * Scroll container interno do SelectionSheet.
 *
 * Em mobile, o Radix Dialog (via DismissableLayer) registra handlers globais
 * de pointer/touch events no document que podem cancelar o scroll nativo dentro
 * de listas longas. A solução é registrar um listener passivo próprio (passive:true
 * sinaliza ao browser que não vai chamar preventDefault) que para a propagação
 * antes do DismissableLayer ver o evento.
 *
 * IMPORTANTE: paramos a propagação APENAS de `touchmove` (o gesto que de fato
 * dispara o scroll). NÃO paramos `touchstart`/`touchend`, porque o React 18
 * delega eventos sintéticos na raiz da app: um stopPropagation aqui impediria o
 * evento de chegar lá e quebraria `onTouchStart`/`onTouchEnd` de descendentes —
 * em especial a react-mobile-picker (TimeWheel), que captura o início do arraste
 * no onTouchStart. Sem ele, o offset inicial fica 0 e a roda "pula" pra fora da
 * área visível ao arrastar. Ver src/components/ui/time-wheel.tsx.
 *
 * Combinado com `touch-action: pan-y`, `overscroll-behavior: contain` e
 * `-webkit-overflow-scrolling: touch` (legado iOS), garante scroll funcional em
 * iOS Safari, Android Chrome e WebView.
 */
function ScrollContainer({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const stop = (e: TouchEvent) => e.stopPropagation()
    el.addEventListener('touchmove', stop, { passive: true })
    return () => {
      el.removeEventListener('touchmove', stop)
    }
  }, [])

  return (
    <div
      ref={ref}
      data-slot="selection-sheet-scroll"
      // `[-webkit-overflow-scrolling:touch]` é o legado iOS pra momentum scroll;
      // sem ele algumas versões antigas de Safari não fazem scroll inertia.
      className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]"
    >
      {children}
    </div>
  )
}

function SearchField({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string
  value?: string
  onChange?: (v: string) => void
}) {
  // Controlled se caller passa value; uncontrolled se não.
  const [internal, setInternal] = React.useState("")
  const effectiveValue = value ?? internal

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (value === undefined) setInternal(e.target.value)
    onChange?.(e.target.value)
  }

  return (
    <div className="px-4 py-2 border-b shrink-0">
      <div className="flex items-center gap-2 border rounded-md px-2 py-1.5 bg-muted/40">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <Input
          value={effectiveValue}
          onChange={handleChange}
          placeholder={placeholder}
          className="h-auto border-0 p-0 text-sm bg-transparent shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  )
}

interface SelectionSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  searchPlaceholder?: string
  onSearchChange?: (value: string) => void
  searchValue?: string
  children: React.ReactNode
  footer?: React.ReactNode
}

export function SelectionSheet({
  open,
  onOpenChange,
  title,
  searchPlaceholder,
  onSearchChange,
  searchValue,
  children,
  footer,
}: SelectionSheetProps) {
  return (
    <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay
          data-slot="sheet-overlay"
          className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[60] bg-black/50"
          )}
        />
        <SheetPrimitive.Content
          data-slot="sheet-content"
          className={cn(
            // 250ms open / 200ms close — antes 500/300 sentia lento.
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-[60] flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-[250ms]",
            // Altura limitada: 10-12 registros + header/busca + footer ≈ 640px. Em telas
            // pequenas usa dvh (mais robusto que vh no Safari mobile com barra dinâmica).
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 max-h-[min(85dvh,640px)] border-t rounded-t-lg"
          )}
        >
          <SheetPrimitive.Close asChild>
            <button
              type="button"
              aria-label="Fechar"
              data-slot="responsive-dialog-handle"
              className="mx-auto mt-2 mb-1 h-1 w-9 rounded-full bg-muted cursor-pointer hover:bg-muted-foreground/30 transition-colors shrink-0"
            />
          </SheetPrimitive.Close>

          {/* Header — SheetPrimitive.Title serves as the accessible label */}
          <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1 -ml-1 rounded hover:bg-muted"
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <SheetPrimitive.Title asChild>
              <h2 className="font-semibold text-base flex-1 min-w-0 truncate">{title}</h2>
            </SheetPrimitive.Title>
          </div>

          {/* Search */}
          {searchPlaceholder !== undefined && (
            <SearchField
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={onSearchChange}
            />
          )}

          <ScrollContainer>{children}</ScrollContainer>

          {/* Footer */}
          {footer && (
            <div className="px-4 py-3 border-t shrink-0">
              {footer}
            </div>
          )}
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  )
}
